import React, { FC, FormEvent, ReactElement, useEffect, useState } from 'react';
import Modal from 'react-modal';
import './LoginModal.sass';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getContentDataIntro,
  getEventKey,
  getUserEmail,
  getUserRegistrationFields,
  getIsRegisterInProgress,
  setLoggedIn,
  setAdminLoggedIn,
  setUserEmail,
  setUserRegistrationFields,
  setIsRegisterInProgress,
} from '../../../stores/reducers/userData';
import { internalizationState } from '../../../stores/reducers/sections';
import routes from '../../../config/routes';
import Icon from '../../Icon';
import { authByEmail, checkAuthorization, loginAdmin, registerToEvent, restorePassword } from '../../../api';
import localeFieldResolver from '../../../helpers/locale-field-finder';
import responseErrorChecker from '../../../helpers/response-error-checker';

Modal.setAppElement('#root');

interface Props {
  isOpen: boolean;
  closeModal?: () => void;
  title?: string;
  notice?: string;
  isAdminAuth?: boolean;
  goal?: string;
  isConfirmRegistrationMode?: boolean;
}

const LoginModal: FC<Props> = ({
  isOpen,
  closeModal,
  title,
  notice,
  isAdminAuth,
  goal,
  isConfirmRegistrationMode,
}): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [email, setEmail] = useState('');
  const emailFromStore = useSelector(getUserEmail);
  const [password, setPassword] = useState('');
  const eventKey = useSelector(getEventKey);
  const userRegistrationFields = useSelector(getUserRegistrationFields);
  const [isRestoringPassword, setIsRestoringPassword] = useState(!isConfirmRegistrationMode);
  const { eventName, eventName_en } = useSelector(getContentDataIntro);
  const localizedEventNames = { eventName, eventName_en };
  const [selfNotice, setSelfNotice] = useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const isRegisterInProgress = useSelector(getIsRegisterInProgress);
  const { activeLangState } = useSelector(internalizationState);

  const localizeData = {
    password_ru: 'Пароль',
    password_en: 'Password',
    get_password_ru: 'Получить пароль',
    get_password_en: 'Get password',
    password_confirm_ru: 'Введите пароль из письма и нажмите "Подтвердить"',
    password_confirm_en: 'Enter password from letter and press "Confirm"',
    login_ru: 'Войти',
    login_en: 'Login',
    login_event_ru: 'Вход на мероприятие',
    login_event_en: 'Entry to the event',
    enter_email_ru: 'Введите email',
    enter_email_en: 'Enter your email',
    wrong_pass_ru: 'Вы ввели неверный пароль',
    wrong_pass_en: 'Incorrect password',
    error_occured_ru: 'Возникла ошибка, повторите попытку позже',
    error_occured_en: 'An error occured, please try again later',
    success_message_ru: `Пароль для входа на мероприятие "${localeFieldResolver(localizedEventNames, 'eventName', activeLangState) as string}" отправлен вам на почту`,
    success_message_en: `The password to enter the event ${localeFieldResolver(localizedEventNames, 'eventName', activeLangState) as string} has been sent to your email`,
    failed_message_ru: 'Email не зарегистрирован на мероприятие. Пройдите регистрацию.',
    failed_message_en: 'Email is not registered for the event. Please register.',
    register_now_ru: 'Зарегистрироваться',
    register_now_en: 'Register now',
    change_email_ru: 'Изменить email',
    change_email_en: 'Change email address',
  };

  const localizedName = (field: string): string => localeFieldResolver(localizeData, field, activeLangState);

  useEffect(() => {
    if (isAdminAuth) {
      setIsRestoringPassword(false);
    }
  }, [isOpen, isAdminAuth]);

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();

    try {
      window.dispatchEvent((window as any).authFinished); 

      if (isAdminAuth) {
        const loginAdminResponse = await loginAdmin(email, password) as Response;
        if (loginAdminResponse.status === 200) {
          dispatch(setAdminLoggedIn(true));
          if (closeModal) closeModal();
        }
      } else {
        const authByEmailResponse = await authByEmail(email || emailFromStore, password, eventKey) as Response;
        const authByEmailPermission = await responseErrorChecker(authByEmailResponse, activeLangState);

        if (!authByEmailPermission) return;

        if (authByEmailResponse.status === 200) {
          if (!isRegisterInProgress) {
            const checkAuthorizationResponse = await checkAuthorization(eventKey) as Response;
            if (checkAuthorizationResponse.status === 403) history.push(routes.REGISTER);
          }

          if (userRegistrationFields && Object.keys(userRegistrationFields).length) {
            const registerToEventResponse = await registerToEvent(userRegistrationFields, eventKey) as Response;
            if (registerToEventResponse.status === 200) {
              dispatch(setUserRegistrationFields([]));
            } else {
              console.error('registerToEvent: bad response!');
            }
          }

          dispatch(setLoggedIn(true));
          dispatch(setUserEmail(email));
          dispatch(setIsRegisterInProgress(false));
          if (closeModal) closeModal();
          history.push(routes.LIVE);
        }
      }
    } catch (error) {
      console.error(`Login error: ${error}`);
    }
  };

  const handleRestorePassword = (e: FormEvent) => {
    setIsSubmitDisabled(true);
    e.preventDefault();
    window.dispatchEvent((window as any).clickRestore);
    restorePassword(email, eventKey).then((response) => {
      if (response) {
        const { status, code } = response;
        if (status) {
          setIsRestoringPassword(false);
          setIsSubmitDisabled(false);
          setSelfNotice(localizedName('success_message_ru'));
        } else if (code === 404) {
          setTimeout(() => {
            setIsSubmitDisabled(false);
          }, 1000);
          setSelfNotice(localizedName('failed_message_ru'));
        }
      }
    });
  };

  const goToRegistration = () => {
    if (closeModal) closeModal();
    history.push(`${routes.REGISTER}`);
  };

  return (
    <Modal
      className="modal-login-content"
      overlayClassName="modal-login-overlay"
      isOpen={isOpen}
      onRequestClose={closeModal}>
      {isRestoringPassword ? (
        <>
          <h3 className="modal-title">{localizedName('login_event_ru')}</h3>
          {closeModal && (
            <button className="modal-close" type="button" onClick={closeModal}>
              <Icon name="close" />
            </button>
          )}
          {(notice || selfNotice) && <p className="modal-notice">{notice || selfNotice}</p>}
          <form className="modal-form" onSubmit={(e) => handleRestorePassword(e)}>
            <input
              id="modalLogin"
              type="email"
              placeholder={localizedName('enter_email_ru')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="login-form-buttons">
              <button type="submit" className="btn btn-primary" disabled={!email || isSubmitDisabled}>
                {localizedName('get_password_ru')}
              </button>
              {!isAdminAuth && (
                <button type="button" className="btn--text" onClick={goToRegistration}>
                  {localizedName('register_now_ru')}
                </button>
              )}
            </div>
          </form>
        </>
      ) : (
        <>
          <h3 className="modal-title">{title || localizedName('login_ru')}</h3>
          {closeModal && (
            <button className="modal-close" type="button" onClick={closeModal}>
              <Icon name="close" />
            </button>
          )}
          {(notice || selfNotice) && <p className="modal-notice" style={{color: '#fe3737'}}>{notice || selfNotice}</p>}
          <form className="modal-form" onSubmit={(e) => handleLogin(e)}>
            {isAdminAuth && (
              <>
                <label htmlFor="modalLogin">{isAdminAuth ? 'Login' : 'Email'}</label>
                <input
                  id="modalLogin"
                  type={isAdminAuth ? 'text' : 'email'}
                  placeholder={isAdminAuth ? 'Login' : 'Email'}
                  value={email || emailFromStore}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </>
            )}

            <label htmlFor="modalPass">{localizedName('password_ru')}</label>
            <input
              id="modalPass"
              type="password"
              placeholder={localizedName('password_confirm_ru')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <div className="login-form-buttons">
              <button type="submit" className="btn btn-primary" disabled={!(email || emailFromStore) || !password}>
                {(() => {
                  switch (activeLangState) {
                    case "ru": return isConfirmRegistrationMode ? 'Подтвердить' : 'Войти';
                    case "en": return isConfirmRegistrationMode ? 'Confirm' : 'Login';
                  }
                })()}
              </button>
              {!(isAdminAuth || isConfirmRegistrationMode) && (
                // <>
                //   <button type="button" className="btn--text" onClick={goToRegistration}>
                //     Зарегистрироваться
                //   </button>
                <button type="button" className="btn--text" onClick={() => setIsRestoringPassword(true)}>
                  {localizedName('change_email_ru')}
                </button>
                // </>
              )}
            </div>
          </form>
        </>
      )}
    </Modal>
  );
};

export default LoginModal;
